import { FC, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Image from 'next/image'
import logo from '@/public/losi-icon.png'
import { IconSparkle2 } from '@tabler/icons-react'
import { Loader2 } from 'lucide-react'
import { TextareaAutosize } from './textarea-autosize'
import { cn } from '@/lib/utils'

interface AIGenerationInputProps {
  onGenerate: (prompt: string) => void
  isGenerating: boolean
  placeholder?: string
  onSkip?: () => void
}

export const AIGenerationInput: FC<AIGenerationInputProps> = ({
  onGenerate,
  isGenerating,
  placeholder,
  onSkip
}) => {
  const { t } = useTranslation()
  const [input, setInput] = useState('')

  const handleSubmit = () => {
    if (input.trim() && !isGenerating) {
      onGenerate(input.trim())
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  // Calculate border radius based on input lines (similar to chat input)
  const borderRadius = useMemo(() => {
    const lines = input.split('\n').length
    if (lines === 1) return 'rounded-3xl'
    if (lines === 2) return 'rounded-2xl'
    return 'rounded-xl'
  }, [input])

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center space-y-6 px-4">
      <div className="flex flex-col items-center space-y-4">
        <Image src={logo} alt="Losi" width={80} height={80} className="rounded-2xl" />
        <h2 className="text-lg font-medium">{t('What would you like to create?')}</h2>
      </div>

      <div className="w-full max-w-2xl">
        <div
          className={cn(
            'bg-input focus-within:bg-background hover:bg-accent/50 relative flex items-end border transition-all',
            borderRadius
          )}
        >
          <TextareaAutosize
            value={input}
            onValueChange={setInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t('Describe what you want to create...')}
            disabled={isGenerating}
            className="placeholder:text-muted-foreground flex-1 resize-none bg-transparent px-4 py-3 pr-12 text-sm outline-none disabled:opacity-50"
            minRows={1}
            maxRows={6}
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || isGenerating}
            className="bg-primary text-primary-foreground hover:bg-primary/90 absolute bottom-1 right-2 rounded-lg p-2 transition-colors disabled:opacity-50"
          >
            {isGenerating ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              <IconSparkle2 className="size-5" />
            )}
          </button>
        </div>

        {onSkip && (
          <button
            onClick={onSkip}
            disabled={isGenerating}
            className="text-muted-foreground hover:text-foreground mt-4 w-full text-center text-sm transition-colors disabled:opacity-50"
          >
            {t('Skip and create manually')}
          </button>
        )}
      </div>
    </div>
  )
}
